import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { t } from '../../i18n/index.js';
import { formatDuration } from '../../arena/useArena.js';
import { playSound } from '../../audio/sounds.js';
import { Board } from './Board.js';
import { ShipSprite } from './ShipSprite.js';
import {
  BOARD_SIZE,
  FLEET_SIZES,
  SHIP_TYPES,
  checkShip,
  forbiddenCells,
  randomFleet,
  shipTypeForSize,
  validateFleet,
  type Cell,
  type Orientation,
  type ShipPlacement,
} from '../../../shared/games/battleship/index.js';

type Slots = (ShipPlacement | null)[];

/**
 * Places the ship so that its cell number `grab` (0 = bow) is under the pointer, keeping it on the board.
 * Without `grab` the ship is centred on the pointer.
 */
function anchored(cell: Cell, size: number, orientation: Orientation, grab?: number): ShipPlacement {
  const back = Math.min(size - 1, grab ?? Math.floor((size - 1) / 2));
  const clamp = (v: number) => Math.max(0, Math.min(BOARD_SIZE - size, v));
  return orientation === 'h'
    ? { size, orientation, x: clamp(cell.x - back), y: cell.y }
    : { size, orientation, x: cell.x, y: clamp(cell.y - back) };
}

function shipAt(slots: Slots, cell: Cell): number {
  return slots.findIndex(
    (s) =>
      s !== null &&
      (s.orientation === 'h'
        ? s.y === cell.y && cell.x >= s.x && cell.x < s.x + s.size
        : s.x === cell.x && cell.y >= s.y && cell.y < s.y + s.size),
  );
}

/**
 * Fleet placement (suunnitelma.md sections 11–12). The rules are checked immediately in the
 * browser for feedback; the server validates the final fleet independently.
 */
export function PlacementView(props: {
  forbiddenAreaVisible: boolean;
  deadline: number;
  serverNow: number;
  opponentReady: boolean;
  onReady: (fleet: ShipPlacement[]) => Promise<string | null>;
}) {
  const [slots, setSlots] = useState<Slots>(() => FLEET_SIZES.map(() => null));
  /** Ship "in hand" (from the dock, or lifted off the board by dragging); its slot is empty meanwhile. */
  const [selected, setSelected] = useState<number | null>(0);
  /** Placed ship that is highlighted and can be rotated in place or removed. */
  const [active, setActive] = useState<number | null>(null);
  const [orientation, setOrientation] = useState<Orientation>('h');
  const [hover, setHover] = useState<Cell | null>(null);
  /** Which cell of the ship in hand is under the pointer (null = centred). */
  const [grab, setGrab] = useState<number | null>(null);
  /** Pointer pressed on a placed ship; it is lifted only once the pointer moves to another cell. */
  const [press, setPress] = useState<{ index: number; cell: Cell } | null>(null);
  /** Original position of a ship lifted off the board (restored if dropped on an invalid spot). */
  const [lifted, setLifted] = useState<{ index: number; ship: ShipPlacement } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const placed = slots.filter((s): s is ShipPlacement => s !== null);
  const candidate = selected !== null && hover ? anchored(hover, FLEET_SIZES[selected]!, orientation, grab ?? undefined) : null;
  const candidateValid = candidate !== null && checkShip(placed, candidate) === null;
  const remaining = slots.filter((s) => s === null).length;
  const shaded = useMemo(() => (props.forbiddenAreaVisible ? forbiddenCells(placed) : []), [props.forbiddenAreaVisible, slots]);
  const activeShip = active !== null ? slots[active] ?? null : null;
  const shownOrientation = selected === null && activeShip ? activeShip.orientation : orientation;

  const withSlot = (list: Slots, index: number, ship: ShipPlacement | null) => list.map((s, i) => (i === index ? ship : s));

  /** Rotates a placed ship where it is: first around its bow, then around its centre. */
  const rotatePlaced = (index: number) => {
    const ship = slots[index]!;
    const others = slots.filter((s, i): s is ShipPlacement => s !== null && i !== index);
    const flipped: Orientation = ship.orientation === 'h' ? 'v' : 'h';
    const centre: Cell =
      ship.orientation === 'h'
        ? { x: ship.x + Math.floor((ship.size - 1) / 2), y: ship.y }
        : { x: ship.x, y: ship.y + Math.floor((ship.size - 1) / 2) };
    const options = [anchored({ x: ship.x, y: ship.y }, ship.size, flipped, 0), anchored(centre, ship.size, flipped)];
    const fit = options.find((o) => checkShip(others, o) === null);
    if (!fit) {
      playSound('placeInvalid');
      setError(t.battleship.cannotRotate);
      return;
    }
    playSound('rotate');
    setError(null);
    setSlots(withSlot(slots, index, fit));
  };

  const rotate = () => {
    if (selected !== null) {
      playSound('rotate');
      setOrientation((o) => (o === 'h' ? 'v' : 'h'));
    } else if (active !== null && slots[active]) {
      rotatePlaced(active);
    }
  };
  const chooseOrientation = (o: Orientation) => {
    if (o !== shownOrientation) rotate();
  };
  const selectShip = (index: number) => {
    playSound('shipSelect');
    setSelected(index);
    setActive(null);
    setGrab(null);
    setError(null);
  };
  const activate = (index: number) => {
    playSound('shipSelect');
    setSelected(null);
    setActive(index);
    setError(null);
  };

  /** Removes the highlighted ship from the board; it returns to the dock (user request 2026-09-27). */
  const remove = () => {
    if (active === null || !slots[active]) return;
    playSound('fleetClear');
    setSlots(withSlot(slots, active, null));
    setActive(null);
    setError(null);
  };

  /** Puts the ship in hand down; after placing nothing follows the pointer (user feedback 2026-09-27). */
  const place = () => {
    if (selected === null || !candidate) return;
    if (!candidateValid) {
      playSound('placeInvalid');
      return;
    }
    playSound('shipPlace');
    setSlots(withSlot(slots, selected, candidate));
    setActive(selected);
    setSelected(null);
    setGrab(null);
    setError(null);
  };

  /** Cancels a drag: the lifted ship returns to where it was. */
  const restoreLifted = () => {
    if (!lifted) return;
    setSlots(withSlot(slots, lifted.index, lifted.ship));
    setActive(lifted.index);
    setSelected(null);
    setGrab(null);
    setLifted(null);
  };

  const cancel = () => {
    if (lifted) restoreLifted();
    else if (selected !== null) setSelected(null);
    else setActive(null);
  };

  // Keyboard: R rotates, Delete/Backspace removes the highlighted ship, Esc cancels.
  const keys = useRef({ rotate, remove, cancel });
  keys.current = { rotate, remove, cancel };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.closest?.('input, textarea')) return;
      if (e.key === 'r' || e.key === 'R') keys.current.rotate();
      if (e.key === 'Delete' || e.key === 'Backspace') keys.current.remove();
      if (e.key === 'Escape') keys.current.cancel();
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  const onPointer = (type: 'down' | 'move' | 'up' | 'leave', cell: Cell | null, e: PointerEvent) => {
    if (type === 'leave') {
      if (e.pointerType === 'mouse' && !lifted) setHover(null);
      return;
    }
    if (type === 'down') {
      setHover(cell);
      const index = cell ? shipAt(slots, cell) : -1;
      if (index >= 0) {
        // Pressing a placed ship highlights it; dragging then moves it.
        setPress({ index, cell: cell! });
        if (active !== index || selected !== null) activate(index);
      } else if (selected === null) {
        setActive(null);
      }
      return;
    }
    if (type === 'move') {
      setHover(cell);
      if (press && !lifted && cell && (cell.x !== press.cell.x || cell.y !== press.cell.y)) {
        const ship = slots[press.index]!;
        const offset = ship.orientation === 'h' ? press.cell.x - ship.x : press.cell.y - ship.y;
        setLifted({ index: press.index, ship });
        setSlots(withSlot(slots, press.index, null));
        setSelected(press.index);
        setActive(null);
        setOrientation(ship.orientation);
        setGrab(offset);
      }
      return;
    }
    // up
    setPress(null);
    if (lifted) {
      if (!cell) {
        // Dragged off the board: the ship goes back to the dock.
        playSound('fleetClear');
        setSelected(null);
        setGrab(null);
        setLifted(null);
      } else if (candidateValid) {
        place();
        setLifted(null);
      } else {
        playSound('placeInvalid');
        restoreLifted();
      }
    } else if (!press && selected !== null && cell) {
      place();
    }
    if (e.pointerType !== 'mouse') setHover(null);
  };

  const randomize = () => {
    playSound('fleetRandom');
    setSlots(randomFleet());
    setSelected(null);
    setActive(null);
    setLifted(null);
    setError(null);
  };

  const clear = () => {
    playSound('fleetClear');
    setSlots(FLEET_SIZES.map(() => null));
    setSelected(0);
    setActive(null);
    setLifted(null);
    setGrab(null);
    setError(null);
  };

  const ready = async () => {
    if (!validateFleet(placed).ok) return;
    setBusy(true);
    const err = await props.onReady(placed);
    if (!err) playSound('fleetReady');
    setError(err);
    setBusy(false);
  };

  const boardShips = slots.flatMap((s, i) =>
    s ? [{ ship: s, class: `own ${i === active ? 'selected' : ''}` }] : [],
  );

  const dockButton = (i: number, size: number) => (
    <button
      class={`dock-ship size-${size} ${slots[i] ? 'placed' : ''} ${selected === i || active === i ? 'selected' : ''}`}
      onClick={() => (slots[i] ? activate(i) : selectShip(i))}
      aria-pressed={selected === i || active === i}
      aria-label={t.battleship.shipNames[shipTypeForSize(size)!]}
    >
      <ShipSprite size={size} orientation="h" />
    </button>
  );
  const shipsOfSize = (size: number) => FLEET_SIZES.map((s, i) => (s === size ? i : -1)).filter((i) => i >= 0);

  return (
    <div class="placement">
      <div class="placement-board">
        {/* Phones: a compact dock right above the board so choosing and placing fit on one screen. */}
        <div class="dock-strip mobile-only">
          {SHIP_TYPES.flatMap((type) => shipsOfSize(type.size).map((i) => dockButton(i, type.size)))}
        </div>
        {/* The orientation is chosen before the ship is brought to the board (user feedback 2026-09-27). */}
        <div class="orientation-bar">
          <span class="orientation-ship">
            {(selected ?? active) !== null ? (
              <>
                <ShipSprite size={FLEET_SIZES[(selected ?? active)!]!} orientation="h" />
                {t.battleship.shipNames[shipTypeForSize(FLEET_SIZES[(selected ?? active)!]!)!]}
              </>
            ) : (
              <span class="muted">{t.battleship.chooseShip}</span>
            )}
          </span>
          <div class="segmented" role="group" aria-label={t.battleship.orientation}>
            <button class={shownOrientation === 'h' ? 'active' : ''} aria-pressed={shownOrientation === 'h'} onClick={() => chooseOrientation('h')}>
              ↔ {t.battleship.horizontal}
            </button>
            <button class={shownOrientation === 'v' ? 'active' : ''} aria-pressed={shownOrientation === 'v'} onClick={() => chooseOrientation('v')}>
              ↕ {t.battleship.vertical}
            </button>
          </div>
          <button
            class="btn btn-small mobile-only"
            onClick={remove}
            disabled={active === null}
            title={t.battleship.removeShip}
            aria-label={t.battleship.removeShip}
          >
            🗑
          </button>
        </div>
        <Board
          ships={boardShips}
          shaded={shaded}
          preview={candidate ? { ship: candidate, valid: candidateValid } : null}
          onPointer={onPointer}
          class="board-large"
        />
      </div>

      <aside class="card placement-panel">
        <div class="section-head">
          <h2>{t.battleship.placementTitle}</h2>
          <div class="deadline">
            <span class="muted">{t.battleship.timeLeft}</span>
            <strong>{formatDuration(props.deadline - props.serverNow)}</strong>
          </div>
        </div>
        <p class="muted help">
          <span class="help-mouse">{t.battleship.placementHelp}</span>
          <span class="help-touch">{t.battleship.placementHelpTouch}</span>
        </p>

        <div class="dock desktop-only">
          {SHIP_TYPES.map((type) => {
            return (
              <div class="dock-row">
                <div class="dock-label">
                  {t.battleship.shipNames[type.type]} <span class="muted">({type.size})</span>
                </div>
                <div class="dock-ships">
                  {shipsOfSize(type.size).map((i) => dockButton(i, type.size))}
                </div>
              </div>
            );
          })}
        </div>

        <div class="placement-actions">
          <button class="btn" onClick={rotate} disabled={selected === null && active === null}>
            ⟳ {t.battleship.rotate} ({shownOrientation === 'h' ? '↔' : '↕'})
          </button>
          <button class="btn" onClick={remove} disabled={active === null}>
            🗑 {t.battleship.removeShip}
          </button>
          <button class="btn" onClick={randomize}>
            {t.battleship.random}
          </button>
          <button class="btn btn-ghost" onClick={clear}>
            {t.battleship.clear}
          </button>
        </div>

        <p class="muted">{t.battleship.shipsLeftToPlace(remaining)}</p>
        <p class="muted">{props.opponentReady ? t.battleship.opponentReady : t.battleship.opponentPlacing}</p>
        {error && <p class="error">{error}</p>}
        <button class="btn btn-primary btn-block" onClick={ready} disabled={remaining > 0 || busy}>
          {t.battleship.ready}
        </button>
        <p class="hint">{t.game.placementInfo}</p>
      </aside>
    </div>
  );
}
